import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { User, Package, LogOut, ChevronRight, KeyRound, Eye, EyeOff, Loader2, Save, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPassword, setHasPassword] = useState(null); // null = unknown, true/false from /me
  const [statusFilter, setStatusFilter] = useState('all');
  const [reorderingId, setReorderingId] = useState(null);

  // Change password dialog
  const [isPwdDialogOpen, setIsPwdDialogOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPwd, setChangingPwd] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [ordersRes, meRes] = await Promise.all([
          axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        // Only show orders that the user actually paid for (skip pending_payment)
        const visible = (ordersRes.data || []).filter(o => o.status !== 'pending_payment' || o.payment_status === 'paid');
        setOrders(visible);
        setHasPassword(meRes.data?.has_password ?? true);
      } catch (error) {
        console.error('Error fetching account data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, navigate]);

  const handleReorder = async (orderId) => {
    setReorderingId(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/reorder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added, skipped } = res.data;
      if (added > 0) {
        toast.success(`${added} item${added > 1 ? 's' : ''} added to your basket`);
        if (skipped?.length) {
          toast.warning(`Skipped: ${skipped.join(', ')}`);
        }
        navigate('/cart');
      } else {
        toast.error('Could not reorder — items no longer available');
      }
    } catch (e) {
      toast.error('Failed to reorder');
    } finally {
      setReorderingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSavePassword = async () => {
    const { current_password, new_password, confirm_password } = pwdForm;
    if (hasPassword && !current_password) {
      toast.error('Please enter your current password');
      return;
    }
    if (!new_password || !confirm_password) {
      toast.error('Please fill in the new password and confirmation');
      return;
    }
    if (new_password.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    if (new_password !== confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current_password || '', new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(hasPassword ? 'Password updated successfully' : 'Password set! You can now sign in with email & password.');
      setIsPwdDialogOpen(false);
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setHasPassword(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update password');
    } finally {
      setChangingPwd(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending_payment':
        return 'bg-amber-100 text-amber-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-stone-100 text-stone-800';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="account-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
            My Account
          </h1>
          <p className="text-stone-500 mt-2">Manage your account and view your orders</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#8D6E63]/10 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-[#8D6E63]" />
                </div>
                <div>
                  <h2 className="font-semibold text-[#1a1a1a]">{user.name}</h2>
                  <p className="text-stone-500 text-sm">{user.email}</p>
                </div>
              </div>

              {user.phone && (
                <div className="mb-6 p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-500">Phone</p>
                  <p className="font-medium text-[#1a1a1a]">{user.phone}</p>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>

              <Button
                variant="outline"
                className="w-full rounded-full border-stone-300 text-stone-700 hover:bg-stone-100 mt-3"
                onClick={() => { setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); setIsPwdDialogOpen(true); }}
                data-testid="change-password-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {hasPassword === false ? 'Set a Password' : 'Change Password'}
              </Button>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm mt-6"
            >
              <h3 className="font-semibold text-[#1a1a1a] mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link 
                  to="/shop" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <span className="text-stone-600">Continue Shopping</span>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </Link>
                <Link 
                  to="/allergens" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <span className="text-stone-600">Allergen Info</span>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </Link>
                <Link 
                  to="/contact" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <span className="text-stone-600">Contact Us</span>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* All Orders */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a]">
                  Your Orders
                </h2>
                {orders.length > 0 && (
                  <span className="text-xs text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
                    {orders.length} total
                  </span>
                )}
              </div>

              {/* Status filter chips */}
              {!loading && orders.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5 -mx-1 px-1 overflow-x-auto pb-1">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'confirmed', label: 'Confirmed' },
                    { id: 'preparing', label: 'Preparing' },
                    { id: 'ready', label: 'Ready' },
                    { id: 'collected', label: 'Collected' },
                  ].map((f) => {
                    const count = f.id === 'all' ? orders.length : orders.filter(o => o.status === f.id).length;
                    const isActive = statusFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setStatusFilter(f.id)}
                        disabled={count === 0 && f.id !== 'all'}
                        className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap ${
                          isActive
                            ? 'bg-[#1a1a1a] text-white'
                            : count === 0 && f.id !== 'all'
                              ? 'bg-stone-50 text-stone-300 cursor-not-allowed'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                        data-testid={`filter-${f.id}`}
                      >
                        {f.label} {count > 0 && <span className="opacity-70">({count})</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-stone-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No orders yet</p>
                  <Link to="/shop">
                    <Button className="mt-4 rounded-full bg-[#1a1a1a] hover:bg-[#1a1a1a]/90">
                      Start Shopping
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders
                    .filter(o => statusFilter === 'all' || o.status === statusFilter)
                    .map((order) => {
                      const firstImg = order.items?.[0]?.image && getImageUrl(order.items[0].image);
                      return (
                        <div
                          key={order.id}
                          className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border border-stone-100 rounded-xl hover:bg-stone-50 hover:border-stone-200 transition-all"
                          data-testid={`order-${order.id}`}
                        >
                          <Link
                            to={`/account/orders/${order.id}`}
                            className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0"
                          >
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0">
                              {firstImg ? (
                                <img
                                  src={firstImg}
                                  alt="Order"
                                  loading="lazy"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-6 h-6 text-stone-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#1a1a1a] truncate">
                                {order.items?.map(i => i.product_name).join(', ') || 'Order'}
                              </p>
                              <p className="text-xs text-stone-500 mt-0.5">
                                #{order.order_number || order.id?.slice(0, 8)}
                              </p>
                              <p className="text-sm text-stone-600 mt-1">
                                {order.created_at && format(new Date(order.created_at), 'd MMM yyyy')} • <span className="font-medium">€{order.total?.toFixed(2)}</span>
                              </p>
                            </div>
                          </Link>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${getStatusColor(order.status)}`}>
                              {(order.status || '').replace('_', ' ')}
                            </span>
                            <button
                              onClick={() => handleReorder(order.id)}
                              disabled={reorderingId === order.id}
                              className="text-[10px] sm:text-xs font-medium text-[#8D6E63] hover:text-[#1a1a1a] flex items-center gap-1 transition-colors disabled:opacity-50"
                              data-testid={`reorder-${order.id}`}
                            >
                              {reorderingId === order.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                              Reorder
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* ============ CHANGE / SET PASSWORD DIALOG ============ */}
      <Dialog open={isPwdDialogOpen} onOpenChange={setIsPwdDialogOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-[#8D6E63]" />
              {hasPassword === false ? 'Set a Password' : 'Change Password'}
            </DialogTitle>
            <DialogDescription>
              {hasPassword === false
                ? "You signed in with Google. Setting a password gives you a backup way to sign in if you can't access Google."
                : "Choose a strong password with at least 8 characters."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {hasPassword !== false && (
              <div>
                <Label className="text-sm">Current password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    value={pwdForm.current_password}
                    onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                    placeholder="Type your current password"
                    data-testid="current-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    tabIndex={-1}
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm">New password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={pwdForm.new_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                  placeholder="At least 8 characters"
                  data-testid="new-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm">Confirm new password</Label>
              <Input
                type={showNew ? 'text' : 'password'}
                value={pwdForm.confirm_password}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                placeholder="Type the new password again"
                data-testid="confirm-password-input"
                className="mt-1"
              />
              {pwdForm.confirm_password && pwdForm.new_password !== pwdForm.confirm_password && (
                <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsPwdDialogOpen(false)} disabled={changingPwd}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePassword}
              disabled={changingPwd}
              className="bg-[#8D6E63] hover:bg-[#8D6E63]/90"
              data-testid="submit-password-change-btn"
            >
              {changingPwd ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> {hasPassword === false ? 'Set Password' : 'Update Password'}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
